import { useEffect, useRef, useState } from "react";

export const GlobalErrorListener = () => {
  const [pendingError, setPendingError] = useState<Error | null>(null);
  const pendingErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track which error instance we've already thrown so we don't re-throw the same
  // error on subsequent renders (e.g. when an error boundary resets in-place without
  // unmounting this component). A new Error instance from the next event will differ
  // by reference, so the guard lets future errors through correctly.
  const thrownErrorRef = useRef<Error | null>(null);

  useEffect(() => {
    const handleGlobalError = (event: CustomEvent) => {
      const { error } = event.detail;
      if (pendingErrorTimeoutRef.current) {
        clearTimeout(pendingErrorTimeoutRef.current);
      }

      pendingErrorTimeoutRef.current = setTimeout(() => {
        pendingErrorTimeoutRef.current = null;
        setPendingError(error);
      }, 0);
    };

    window.addEventListener("simulate-global-error", handleGlobalError as EventListener);

    return () => {
      window.removeEventListener("simulate-global-error", handleGlobalError as EventListener);
      if (pendingErrorTimeoutRef.current) {
        clearTimeout(pendingErrorTimeoutRef.current);
      }
    };
  }, []);

  if (pendingError && thrownErrorRef.current !== pendingError) {
    thrownErrorRef.current = pendingError;
    throw pendingError;
  }

  return null;
};
